import {
  Typography,
  Box,
  ImageList,
  ImageListItem,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { Star, StarBorder } from '@mui/icons-material';
import { getDownloadURL, ref as storageRef, listAll } from 'firebase/storage';
import { storage } from '../../plugins/firebase';
import { useSelector } from 'react-redux';
import { selectUserUid } from '../../store/slices/userSlice';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUpdateEntryMutation } from '../../hooks/useFirebaseQueries';

const Gallery = ({ entry }) => {
  const userUid = useSelector(selectUserUid);
  const queryClient = useQueryClient();
  const updateEntryMutation = useUpdateEntryMutation();

  const handleSetDefaultImage = async (imageName) => {
    try {
      await updateEntryMutation.mutateAsync({
        entryId: entry.entryId,
        entryData: {
          ...entry,
          defaultImage: imageName
        },
        userDisplayName: 'User', // You might want to get this from user context
        userPhotoURL: ''
      });
      
      // Invalidate and refetch entry data to update UI
      queryClient.invalidateQueries(['entries']);
    } catch (error) {
      console.error('Failed to set default image:', error);
    }
  };

  // Hook to get all images for an entry
  const {
    data: images = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['entryImages', userUid, entry?.entryId],
    queryFn: async () => {
      if (!userUid || !entry?.entryId) return [];

      try {
        // Create reference to the entry's image folder
        const entryImagesRef = storageRef(
          storage,
          `${userUid}/${entry.entryId}`
        );

        console.log(
          'Fetching images from path:',
          `${userUid}/${entry.entryId}`
        );

        // List all files in the folder
        const result = await listAll(entryImagesRef);

        // Check if there are subfolders and list their contents too
        let allItems = [...result.items];

        if (result.prefixes.length > 0) {
          console.log('Found subfolders, listing their contents...');
          for (const prefix of result.prefixes) {
            try {
              const subResult = await listAll(prefix);
              console.log(
                `Contents of ${prefix.fullPath}:`,
                subResult.items.map((item) => item.name)
              );
              allItems.push(...subResult.items);
            } catch (subError) {
              console.warn(
                `Could not list contents of ${prefix.fullPath}:`,
                subError
              );
            }
          }
        }

        // Get download URLs for all images
        const imageUrls = await Promise.all(
          allItems.map(async (imageRef) => {
            try {
              const url = await getDownloadURL(imageRef);
              return {
                url,
                name: imageRef.name,
                fullPath: imageRef.fullPath
              };
            } catch (urlError) {
              console.warn(
                `Could not get download URL for ${imageRef.fullPath}:`,
                urlError
              );
              return null;
            }
          })
        );

        // Filter out failed URLs
        const validImageUrls = imageUrls.filter((img) => img !== null);

        console.log('Valid image URLs retrieved:', validImageUrls.length);
        return validImageUrls;
      } catch (error) {
        console.error('Error fetching entry images:', error);
        return [];
      }
    },
    enabled: !!userUid && !!entry?.entryId,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

  if (!entry?.entryId) {
    return null;
  }

  if (isLoading) {
    return (
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography color="error" variant="body2">
          Error loading images
        </Typography>
      </Box>
    );
  }

  if (images.length === 0) {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          No images found
        </Typography>
      </Box>
    );
  }

  console.log('Rendering Gallery with images:', images.length, images);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Gallery ({images.length})
      </Typography>
      {images.length > 0 ? (
        <ImageList
          cols={3}
          gap={8}
          sx={{
            width: '100%',
            maxHeight: 400,
            overflowY: 'auto'
          }}
        >
          {images.map((image, index) => {
            const isDefault = entry.defaultImage === image.name;
            
            return (
              <ImageListItem 
                key={image.fullPath || `image-${index}`}
                sx={{ 
                  position: 'relative',
                  '&:hover .star-button': {
                    opacity: 1
                  }
                }}
              >
                <img
                  src={image.url}
                  alt={`Entry ${entry.entryId} - ${image.name}`}
                  loading="lazy"
                  style={{
                    borderRadius: 8,
                    objectFit: 'cover',
                    cursor: 'pointer',
                    height: 120,
                    width: '100%',
                    border: isDefault ? '3px solid #ffd700' : 'none'
                  }}
                  onClick={() => window.open(image.url, '_blank')}
                  onError={(e) =>
                    console.error('Image failed to load:', image.url, e)
                  }
                  onLoad={() =>
                    console.log('Image loaded successfully:', image.name)
                  }
                />
                <Tooltip title={isDefault ? 'Default image' : 'Set as default image'}>
                  <IconButton
                    className="star-button"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'transparent',
                      color: isDefault ? '#ffd700' : '#fff',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                      width: 32,
                      height: 32,
                      opacity: isDefault ? 1 : 0,
                      transition: 'opacity 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        transform: 'scale(1.1)'
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetDefaultImage(image.name);
                    }}
                    disabled={updateEntryMutation.isLoading}
                  >
                    {isDefault ? <Star /> : <StarBorder />}
                  </IconButton>
                </Tooltip>
              </ImageListItem>
            );
          })}
        </ImageList>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No images to display
        </Typography>
      )}
    </Box>
  );
};

export default Gallery;
